import {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import {
  exportAdoptionReportToCSV,
  exportAdoptionReportToXLSX,
} from '../../utils/adoptionReportExport';
import {
  getAdoptionReportByBranch,
  getBranchNames,
} from '../../services/rogerDocApi';
import {
  type BranchReportEntry,
  processBranchReportData,
} from '../../types/adoptionReport.types';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import VisitCompletionByClinicianAndBranchReportHeader from './VisitCompletionByClinicianAndBranchReportHeader';
import VisitCompletionByClinicianAndBranchReportTable from './VisitCompletionByClinicianAndBranchReportTable';
import { AdoptionReportExportFormatDialog } from './AdoptionReportExportFormatDialog';

export interface VisitCompletionByClinicianAndBranchRef {
  exportToCSV: () => void;
}

export interface VisitCompletionByClinicianAndBranchProps {
  onExportRegistry?: (exportFunc: (() => void) | null) => void;
}

const VisitCompletionByClinicianAndBranch = forwardRef<
  VisitCompletionByClinicianAndBranchRef,
  VisitCompletionByClinicianAndBranchProps
>(({ onExportRegistry }, ref) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [reportData, setReportData] = useState<BranchReportEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [branchesLoading, setBranchesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBreakdown, setShowBreakdown] = useState(() => {
    const saved = localStorage.getItem('adoptionReportBranch_showBreakdown');
    return saved ? JSON.parse(saved) : false;
  });
  const [hideNeverLoggedIn, setHideNeverLoggedIn] = useState(() => {
    const saved = localStorage.getItem(
      'adoptionReportBranch_hideNeverLoggedIn'
    );
    return saved ? JSON.parse(saved) : false;
  });
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  const [branchOptions, setBranchOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [selectedBranches, setSelectedBranches] = useState<string[]>([]);

  const requestIdRef = useRef(0);

  const [exportFormatDialogOpen, setExportFormatDialogOpen] = useState(false);

  const { showError } = useStore(
    useShallow(state => ({
      showError: state.showError,
    }))
  );

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        setBranchesLoading(true);
        const data = await getBranchNames();

        const options = Object.entries(data).map(([id, name]) => ({
          value: id,
          label: name,
        }));

        options.sort((a, b) => a.label.localeCompare(b.label));

        setBranchOptions(options);
        setSelectedBranches(options.map(o => o.value));
      } catch (err) {
        console.error('Error loading branches:', err);
        showError('Failed to load branch list');
      } finally {
        setBranchesLoading(false);
      }
    };

    void fetchBranches();
  }, [showError]);

  useEffect(() => {
    if (branchesLoading) {
      return;
    }

    if (selectedBranches.length === 0) {
      requestIdRef.current += 1;
      setReportData([]);
      setLoading(false);
      setError(null);
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    setReportData([]);

    const fetchReportData = async () => {
      try {
        const data = await getAdoptionReportByBranch(
          selectedBranches,
          fromDate,
          toDate
        );
        if (currentRequestId !== requestIdRef.current) {
          return;
        }
        const processedData = processBranchReportData(data);
        setReportData(processedData);
      } catch (err) {
        if (currentRequestId !== requestIdRef.current) {
          return;
        }
        console.error('Error loading adoption report by branch:', err);
        setError('Failed to load report data. Please try again.');
        setReportData([]);
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    void fetchReportData();
  }, [selectedBranches, fromDate, toDate, branchesLoading]);

  const getFilenameBase = () =>
    `roger_adoption_report_by_branch_${new Date().toISOString().split('T')[0]}`;

  const exportAsCSV = () => {
    exportAdoptionReportToCSV(reportData, getFilenameBase(), true);
  };

  const exportAsXLSX = async () => {
    try {
      await exportAdoptionReportToXLSX(
        reportData,
        getFilenameBase(),
        true,
        'Adoption Report by Branch'
      );
    } catch (err) {
      console.error('Error exporting adoption report by branch to XLSX:', err);
      showError('Failed to export report. Please try again.');
    }
  };

  const openExportDialog = useCallback(() => {
    setExportFormatDialogOpen(true);
  }, []);

  const handleExportConfirm = (format: 'csv' | 'xlsx') => {
    if (format === 'xlsx') {
      void exportAsXLSX();
    } else {
      exportAsCSV();
    }
  };

  useImperativeHandle(ref, () => ({
    exportToCSV: openExportDialog,
  }));

  useEffect(() => {
    if (onExportRegistry) {
      if (loading || reportData.length === 0) {
        onExportRegistry(null);
      } else {
        onExportRegistry(openExportDialog);
      }
    }
  }, [loading, reportData.length, onExportRegistry, openExportDialog]);

  return (
    <>
      <AdoptionReportExportFormatDialog
        open={exportFormatDialogOpen}
        onOpenChange={setExportFormatDialogOpen}
        onConfirm={handleExportConfirm}
      />

      <div className='flex flex-col h-full'>
        <VisitCompletionByClinicianAndBranchReportHeader
          branchOptions={branchOptions}
          selectedBranches={selectedBranches}
          onSelectedBranchesChange={setSelectedBranches}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          fromDate={fromDate}
          toDate={toDate}
          onFromDateChange={setFromDate}
          onToDateChange={setToDate}
          showBreakdown={showBreakdown}
          onShowBreakdownChange={setShowBreakdown}
          hideNeverLoggedIn={hideNeverLoggedIn}
          onHideNeverLoggedInChange={setHideNeverLoggedIn}
        />

        <VisitCompletionByClinicianAndBranchReportTable
          data={reportData}
          loading={loading}
          error={error}
          showBreakdown={showBreakdown}
          hideNeverLoggedIn={hideNeverLoggedIn}
          searchTerm={searchTerm}
          branchesLoading={branchesLoading}
          hasBranchSelection={selectedBranches.length > 0}
        />
      </div>
    </>
  );
});

VisitCompletionByClinicianAndBranch.displayName =
  'VisitCompletionByClinicianAndBranch';

export default VisitCompletionByClinicianAndBranch;
